import api from "../services/api";
import { toast } from "toastr";

// ── Script loaders ──────────────────────────────────────────────────────────

const loadEasebuzzScript = () => {
    return new Promise((resolve, reject) => {
        if (window.EasebuzzCheckout) return resolve();
        const script = document.createElement("script");
        script.src = "https://ebz-static.s3.ap-south-1.amazonaws.com/easecheckout/v2.0.0/easebuzz-checkout-v2.min.js";
        script.onload = () => resolve();
        script.onerror = () => reject(new Error("Failed to load Easebuzz script."));
        document.body.appendChild(script);
    });
};

export const loadCashfreeScript = () => {
    return new Promise((resolve, reject) => {
        if (window.Cashfree) return resolve();
        const script = document.createElement("script");
        script.src = "https://sdk.cashfree.com/js/v3/cashfree.js";
        script.async = true;
        script.onload = () => resolve();
        script.onerror = () => reject(new Error("Failed to load Cashfree script."));
        document.body.appendChild(script);
    });
};

// ── Initiate payment (hits backend for access_key / order_id) ──────────────
// `paymentDetails` is whatever your backend payment-initiation endpoint expects
// (txnid, amount, name, email, phone, surl, furl, udf1..udf10, securecode, etc.)
export const initiateEasebuzzPayment = async (paymentDetails) => {
    await loadCashfreeScript();
    try {
        const response = await api.post("/initiate_payment", paymentDetails);

        const accessKey = response.data.accesskey;
        const order_id = response?.data?.order_id || "";
        const enviroment = response?.data?.enviroment || "test";
        const ActivePG = response?.data?.ActivePG || "4";

        if (!accessKey) {
            toast.error("Access key not received from backend.", { autoClose: 2000 });
            throw new Error("Access key not received from backend.");
        }

        await loadEasebuzzScript();

        return {
            enviroment,
            accessKey,
            paymentDetails,
            timestamp: new Date().toISOString(),
            ActivePG,
            order_id,
        };
    } catch (error) {
        toast.error("Payment initiation failed", { autoClose: 2000 });
        throw new Error(error.response?.data?.message || "Payment initiation failed");
    }
};

// ── Open Easebuzz checkout modal ────────────────────────────────────────────
export const openEasebuzzPaymentModal = (enviroment, accessKey, paymentDetails, onResponseCallback) => {
    if (!window.EasebuzzCheckout) {
        throw new Error("Easebuzz script not loaded");
    }

    const easebuzzCheckout = new window.EasebuzzCheckout(accessKey, enviroment);

    const options = {
        access_key: accessKey,
        onResponse: function (response) {
            if (typeof onResponseCallback === "function") onResponseCallback(response);
            setTimeout(() => {
                document
                    .querySelectorAll('[id^="easebuzz-container-"], [id^="easebuzz-checkout-"], iframe[src*="easebuzz"]')
                    .forEach((n) => n.remove());
            }, 300);
        },
        onClose: function () {},
        theme: "#123456",
        ...paymentDetails,
    };
    easebuzzCheckout.initiatePayment(options);
};

// ── Open Cashfree checkout modal ────────────────────────────────────────────
export const openCashfreePaymentModal = async (environment, paymentSessionId, paymentDetails, onResponseCallback) => {
    if (!window.Cashfree) {
        throw new Error("Cashfree SDK not loaded");
    }

    try {
        const cashfree = window.Cashfree({ mode: environment });

        const checkoutOptions = {
            paymentSessionId,
            redirectTarget: "_modal",
            ...paymentDetails,
        };

        const result = await cashfree.checkout(checkoutOptions);

        if (result.error) {
            onResponseCallback(result);
        } else if (result.paymentDetails && Object.keys(result.paymentDetails).length) {
            const response = await api.post("/payment/verify", paymentDetails);
            if (typeof onResponseCallback === "function") onResponseCallback(response.data);
        } else {
            onResponseCallback(result);
        }

        setTimeout(() => {
            document
                .querySelectorAll('iframe[src*="sdk.cashfree.com"], [id*="cashfree"]')
                .forEach((el) => el.remove());
        }, 300);

        return result;
    } catch (error) {
        console.error("Cashfree modal error", error);
        throw error;
    }
};

// ── Verify payment (server-side webhook confirmation lookup) ───────────────
export const verifyPayment = async (transactionData) => {
    try {
        const response = await api.post("/verify_payment", transactionData);
        return response.data;
    } catch (error) {
        throw new Error(error.response?.data?.message || "Payment verification failed");
    }
};